import { callApi, RequestType } from "../fetch";
import { UserModel } from "./user_repository";

// Once a company has switched over, some booking changes can only be made through the booking service
// (EV bays, and changes to a series day). OnePark asks the service about the person before making them.
export type BookingServiceAnswer = { switched: boolean; evEnabled: boolean };

// What the person's company takes through the service. A person the service doesn't know a company for is
// treated as not switched; an unreachable service throws, so the question is asked again rather than
// remembered as a no.
export const askBookingService = async (person: Pick<UserModel, "userId" | "capacityTenantId">): Promise<BookingServiceAnswer> => {
    if (!person.userId || !person.capacityTenantId) return { switched: false, evEnabled: false };
    const response = await callApi<{ data?: { evEnabled?: boolean; supportedOperations?: string[] } }>("bookingApiV2/capabilities",
        { tenantId: person.capacityTenantId, userId: person.userId }, RequestType.POST, { signedIn: true });
    if (response.error && (!response.status || response.status >= 500)) throw new Error("The booking service didn't answer.");
    const data = response.data?.data;
    return { switched: Array.isArray(data?.supportedOperations) && data!.supportedOperations!.includes("bookings/edit"),
        evEnabled: data?.evEnabled === true };
};

export type FollowOptions = { pollMs?: number; polls?: number };
type StatusAnswer = { data?: { status?: string } };

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Asks about a request until it settles: while the car park is still confirming it, or when its answer was
// lost (a 5xx or a timeout after the change may have been saved). Resolves the request's last known status.
export const followRequest = async (statusPath: string, requestId: string, userId: string, status: string | undefined,
    { pollMs = 2000, polls = 15 }: FollowOptions = {}): Promise<string | undefined> => {
    for (let poll = 0; status === "pending" && poll < polls; poll++) {
        await wait(pollMs);
        const answer = await callApi<StatusAnswer>(`bookingApiV2/${statusPath}`, { requestId, userId }, RequestType.POST, { signedIn: true });
        if (!answer.error) status = answer.data?.data?.status;
    }
    return status;
};

// A refusal is clear; anything else (a 5xx, a timeout) may have been saved, so it's followed up.
export const answerUnclear = (response: { error: string | null; status?: number }): boolean =>
    Boolean(response.error) && (!response.status || response.status >= 500);

export const newRequestId = (kind: string) => `onepark-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export type Refusals = Record<string, (name: string) => string>;

const REFUSALS: Refusals = {
    capacity_exhausted: () => "There are no spaces left for that time. Try another time or day.",
    duplicate_booking: (name) => `${name} already has a booking at that time.`,
    booking_limit: (name) => `${name} has reached their booking limit for that week.`,
    weekend_not_allowed: (name) => `${name} can't book on weekends. Choose weekdays only.`,
    future_booking_period: (name) => `That's further ahead than ${name} can book. Choose an earlier date.`,
    past_booking: () => "That time has already passed. Choose a later start.",
    invalid_booking: () => "Those times don't work for a booking. Check the start and end times, then try again.",
    multi_day_not_allowed: (name) => `${name} can't make multi-day bookings. Try a single day instead.`,
    vip_booking_not_required: (name) => `${name} has a permanent space, so they don't need a booking.`,
    provider_refused: (name) => `The car park didn't accept this booking for ${name}. Check their card and plate, then try again.`,
    stale_compatibility_epoch: () => "Booking settings changed a moment ago. Please try again.",
    booking_changed: () => "This booking changed a moment ago. Close it, open it again and try once more.",
    series_changed: () => "This booking series changed a moment ago. Close it, open it again and try once more.",
    booking_occupied: (name) => `${name} is parked right now, so this booking can't be changed.`,
    booking_not_editable: () => "This booking can't be changed any more.",
    booking_not_found: () => "This booking has already been cancelled.",
    compatibility_paused: (name) => `Bookings for ${name}'s company are being updated. Please try again in a few minutes.`,
    forbidden: (name) => `You can't make bookings for ${name}.`,
    no_vehicle_or_card: (name) => `${name} has no registered vehicle or access card, so they couldn't get in. Add one to their profile, then book again.`,
};

// The admin-facing reason for a refusal, with a feature's own wording first.
export const refusalMessage = (code: string | undefined, name: string, fallback: string, overrides: Refusals = {}): string =>
    (code && (overrides[code] ?? REFUSALS[code])?.(name)) || fallback;
